import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

interface SeoConfig { title: string; description: string; canonical?: string; image?: string; noindex?: boolean; type?: string; }

@Injectable({ providedIn: 'root' })
export class SeoService {
  private readonly siteName = 'Vishwas Fotography';
  private readonly siteUrl = 'https://www.vishwasfotography.com';
  private readonly defaultImage = `${this.siteUrl}/assets/photography/wedding/wedding-06.webp`;

  constructor(private title: Title, private meta: Meta, private router: Router, @Inject(DOCUMENT) private document: Document) {
    this.router.events.pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd)).subscribe(event => this.update(event.urlAfterRedirects));
  }

  update(url: string): void {
    const cleanUrl = url.split('?')[0].split('#')[0] || '/';
    const key = cleanUrl.replace(/\/$/, '') || '/';
    let config: SeoConfig;

    if (key === '/') {
      config = { title: 'Vishwas Fotography | Wedding & Yoga Photography', description: 'Vishwas Fotography creates natural, expressive wedding and yoga photography in Bengaluru, Mysuru and beyond.', type: 'website' };
    } else if (key === '/photography') {
      config = { title: 'Photography Portfolio | Vishwas Fotography', description: 'Explore the Vishwas Fotography portfolio across Yoga and Wedding photography. Branding photography is coming soon.', type: 'website' };
    } else if (key === '/photography/yoga') {
      config = { title: 'Yoga Photography | Vishwas Fotography', description: 'Explore expressive yoga photography by Vishwas Fotography, capturing movement, balance, strength and stillness.', type: 'website' };
    } else if (key === '/photography/wedding') {
      config = { title: 'Wedding Photography | Vishwas Fotography', description: 'Explore natural and cinematic wedding photography by Vishwas Fotography, capturing emotions, connections and celebrations.', type: 'website' };
    } else if (key === '/photography/branding') {
      config = { title: 'Branding Photography Coming Soon | Vishwas Fotography', description: 'Branding photography by Vishwas Fotography is coming soon.', noindex: true, type: 'website' };
    } else if (key === '/contact') {
      config = { title: 'Contact | Vishwas Fotography', description: 'Contact Vishwas Fotography for wedding, yoga and photography enquiries.', type: 'website' };
    } else if (key === '/privacy') {
      config = { title: 'Privacy Policy | Vishwas Fotography', description: 'Privacy policy for Vishwas Fotography.', type: 'website' };
    } else if (key === '/terms') {
      config = { title: 'Terms & Conditions | Vishwas Fotography', description: 'Terms and conditions for Vishwas Fotography.', type: 'website' };
    } else {
      config = { title: 'Page Not Found | Vishwas Fotography', description: 'The requested page could not be found.', noindex: true, type: 'website' };
    }

    const canonical = `${this.siteUrl}${key === '/' ? '/' : key}`;
    const image = config.image || this.defaultImage;
    this.title.setTitle(config.title);
    this.meta.updateTag({ name: 'description', content: config.description });
    this.meta.updateTag({ name: 'robots', content: config.noindex ? 'noindex, follow' : 'index, follow' });
    this.meta.updateTag({ property: 'og:type', content: config.type || 'website' });
    this.meta.updateTag({ property: 'og:url', content: canonical });
    this.meta.updateTag({ property: 'og:title', content: config.title });
    this.meta.updateTag({ property: 'og:description', content: config.description });
    this.meta.updateTag({ property: 'og:image', content: image });
    this.meta.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
    this.meta.updateTag({ name: 'twitter:title', content: config.title });
    this.meta.updateTag({ name: 'twitter:description', content: config.description });
    this.meta.updateTag({ name: 'twitter:image', content: image });

    let canonicalLink = this.document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonicalLink) { canonicalLink = this.document.createElement('link'); canonicalLink.rel = 'canonical'; this.document.head.appendChild(canonicalLink); }
    canonicalLink.href = canonical;

    const existing = this.document.head.querySelector('script[data-vp-jsonld]');
    existing?.remove();
    const script = this.document.createElement('script');
    script.type = 'application/ld+json'; script.setAttribute('data-vp-jsonld', 'true');
    script.text = JSON.stringify({
      '@context': 'https://schema.org', '@type': key === '/' ? 'ProfessionalService' : 'WebPage',
      name: config.title, description: config.description, url: canonical, image,
      ...(key === '/' ? { provider: { '@type': 'Person', name: 'Vishwas Fotography', url: this.siteUrl }, areaServed: ['Bengaluru', 'Mysuru', 'Karnataka', 'India'] } : {})
    });
    this.document.head.appendChild(script);
  }
}
